import { StateCreator } from 'zustand';

import {
  Voorraad,
  Voorraadbeheer,
  VoorraadbeheerVerijkt,
  VoorraadClaim,
  VoorraadVerijkt,
} from '../types/Voorraadbeheer';
import { mapDienstregelpuntenToRbc, RBCOnbekend } from '../utils/voorraadbeheer';

import { PlankDataStore } from './types/PlankDataStore';
import {
  AtomicKnoopgebiedMap,
  AtomicKnoopMaterieel,
  AtomicRBCMaterieelMap,
  VoorraadbeheerStore,
  VoorraadMapKnoop,
  VoorraadMapMaterieel,
} from './types/VoorraadbeheerStore';

export const getVoorraadVerijkt = (
  voorraad: Voorraadbeheer,
  drpToRbcMap: { [drp: string]: [string, string, string] } | undefined = undefined,
): VoorraadVerijkt => {
  drpToRbcMap ??= mapDienstregelpuntenToRbc();
  // Onbekend kan dus ook voorkomen als een knoop / dienstregelpunt niet correct gematched kan worden
  const [rbc, knoopgebied, knoop] = drpToRbcMap?.[voorraad.dienstregelpunt] ?? ['Onbekend', 'Onbekend', 'Onbekend'];
  const overOfTekort = voorraad.aanwezig - (voorraad.benodigd + voorraad.nietInzetbaar);
  const claims: VoorraadClaim = Object.fromEntries(
    (voorraad?.claims ?? []).map((claim) => [claim.claimsSoort, claim.aantal]),
  );
  return {
    ...voorraad,
    knoop,
    overOfTekort,
    rbc,
    knoopgebied,
    claims,
    nvt: Boolean(voorraad.aanwezig === 0 && voorraad.benodigd === 0 && overOfTekort === 0),
    inzetten: voorraad.reizigersdienst,
  };
};

const accumulateClaimObject = (claimsTotaal: VoorraadClaim, claims: VoorraadClaim): VoorraadClaim => {
  const keys = Array.from(new Set([...Object.keys(claimsTotaal), ...Object.keys(claims)]));
  return keys.reduce((obj, key) => {
    obj[key] = (claimsTotaal?.[key] ?? 0) + (claims?.[key] ?? 0);
    return obj;
  }, {});
};

const accummulateVoorraad = (
  voorraadTotaal: Voorraad,
  voorraad: Voorraad | (Voorraad & Voorraadbeheer) | VoorraadbeheerVerijkt,
): Voorraad => ({
  aanwezig: voorraadTotaal.aanwezig + voorraad.aanwezig,
  benodigd: voorraadTotaal.benodigd + voorraad.benodigd,
  nietInzetbaar: voorraadTotaal.nietInzetbaar + voorraad.nietInzetbaar,
  verwachtGereed: voorraadTotaal.verwachtGereed + voorraad.verwachtGereed,
  overOfTekort: voorraadTotaal.overOfTekort + voorraad.overOfTekort,
  inzetten: voorraadTotaal.inzetten + voorraad.inzetten,
  claims: accumulateClaimObject(voorraadTotaal.claims, voorraad.claims),
});

const reduceAccummulateVoorraad = (
  voorraad: Voorraad[] | (Voorraad & Voorraadbeheer)[] | VoorraadbeheerVerijkt[],
): Voorraad =>
  voorraad.reduce(
    (obj, _voor) => {
      return { ...accummulateVoorraad(obj, _voor) };
    },
    {
      aanwezig: 0,
      benodigd: 0,
      nietInzetbaar: 0,
      verwachtGereed: 0,
      overOfTekort: 0,
      nvt: false,
      inzetten: 0,
      claims: {},
    },
  );

export function groupVoorraadRBC(materieel: string, voorraad: VoorraadVerijkt[]): AtomicRBCMaterieelMap[] {
  function groupVoorraadMaterieelKnoop(
    rbc: string,
    knoopgebied: string,
    knopen: [string, string[]][],
    knopenVoorraad: VoorraadVerijkt[],
  ): VoorraadbeheerVerijkt[] {
    const knoopMap = Map.groupBy(knopenVoorraad, ({ knoop }) => knoop);
    return knopen.flatMap(([knoop]) => {
      const knoopVoorraad = knoopMap.get(knoop) ?? [];
      return knoop === 'Onbekend'
        ? knoopVoorraad
        : {
            ...reduceAccummulateVoorraad(knoopVoorraad),
            knoop,
            materieel,
            knoopgebied,
            rbc,
            nvt: Boolean(!knoopVoorraad.length),
          };
    });
  }
  function groupVoorraadKnoopgebied(
    rbc: string,
    knoopgebieden: [string, [string, string[]][]][],
    knoopgebiedVoorraad: VoorraadVerijkt[],
  ): AtomicKnoopgebiedMap[] {
    const gebiedMap = Map.groupBy(knoopgebiedVoorraad, ({ knoopgebied }) => knoopgebied);
    return knoopgebieden.map<AtomicKnoopgebiedMap>(([knoopgebied, knopen]) => {
      const gebiedVoorraad = gebiedMap.get(knoopgebied) ?? [];
      return {
        ...reduceAccummulateVoorraad(gebiedVoorraad),
        knoopgebied,
        knopen: groupVoorraadMaterieelKnoop(rbc, knoopgebied, knopen, gebiedVoorraad),
        nvt: Boolean(!gebiedVoorraad.length),
      };
    });
  }

  const rbcMap = Map.groupBy(voorraad, ({ rbc }) => rbc);
  return RBCOnbekend.map(([rbc, knoopgebieden]) => {
    const rbcVoorraad = rbcMap.get(rbc) ?? [];
    return {
      ...reduceAccummulateVoorraad(rbcVoorraad),
      rbc,
      materieel,
      knoopgebieden: groupVoorraadKnoopgebied(rbc, knoopgebieden, rbcVoorraad),
      nvt: Boolean(!rbcVoorraad.length),
    };
  }).filter((rbc) => rbc.rbc !== 'Onbekend' || (rbc.rbc === 'Onbekend' && (rbcMap.get('Onbekend') ?? []).length));
}

// Step 1
export function groupVoorraadMaterieel(voorraad: VoorraadVerijkt[]): Map<string, VoorraadMapMaterieel> {
  const materieelMap = new Map<string, VoorraadMapMaterieel>();

  Map.groupBy(voorraad, ({ materieel }) => materieel).forEach((matVoorraad: VoorraadVerijkt[], materieel: string) => {
    materieelMap.set(materieel, {
      ...reduceAccummulateVoorraad(matVoorraad),
      rbcList: groupVoorraadRBC(materieel, matVoorraad),
      nvt: Boolean(!matVoorraad.length),
    });
  });
  return materieelMap;
}

export function groupVoorraadKnoop(voorraad: VoorraadVerijkt[]): Map<string, VoorraadMapKnoop> {
  function groupVoorraadKnoopMaterieel(knoop: string, knoopVoorraad: VoorraadVerijkt[]): AtomicKnoopMaterieel[] {
    const knoopMaterieel = Map.groupBy(knoopVoorraad, ({ materieel }) => materieel);
    return Array.from(knoopMaterieel.entries()).map(([materieel, knoopMatVoorraad]) => {
      const dienstregelpunten = knoopMatVoorraad;
      return {
        ...reduceAccummulateVoorraad(knoopMatVoorraad),
        knoop,
        materieel,
        dienstregelpunten,
        nvt: Boolean(!dienstregelpunten.length),
      };
    });
  }
  const knoopMap = new Map<string, VoorraadMapKnoop>();
  Map.groupBy(voorraad, ({ knoop }) => knoop).forEach((_voorraad, knoop) => {
    knoopMap.set(knoop, {
      ...reduceAccummulateVoorraad(_voorraad),
      matList: groupVoorraadKnoopMaterieel(knoop, _voorraad),
      nvt: Boolean(!_voorraad.length),
    });
  });

  return knoopMap;
}

const voorraadbeheerStateCreator: StateCreator<VoorraadbeheerStore & Partial<PlankDataStore>> = (set, get, __) => ({
  voorraad: [],
  voorraadMaterieel: new Map(),
  voorraadKnoop: new Map(),
  async setVoorraad(voorraad: Voorraadbeheer[]) {
    const drpToRbc = mapDienstregelpuntenToRbc();
    const voorraadVerrijkt = voorraad.map((_voorraad) => getVoorraadVerijkt(_voorraad, drpToRbc));
    const voorraadMaterieel = groupVoorraadMaterieel(voorraadVerrijkt);
    const voorraadKnoop = groupVoorraadKnoop(voorraadVerrijkt);
    set({
      voorraadMaterieel,
      voorraadKnoop,
    });
  },
  getVoorraadMaterieel(matType: string) {
    return (
      get().voorraadMaterieel.get(matType) ?? {
        aanwezig: 0,
        nietInzetbaar: 0,
        verwachtGereed: 0,
        benodigd: 0,
        overOfTekort: 0,
        knopen: [],
        rbcList: [],
        nvt: true,
        inzetten: 0,
        claims: {},
      }
    );
  },
  getVoorraadKnoop(knoop: string) {
    return (
      get().voorraadKnoop.get(knoop) ?? {
        aanwezig: 0,
        nietInzetbaar: 0,
        verwachtGereed: 0,
        benodigd: 0,
        overOfTekort: 0,
        matList: [],
        inzetten: 0,
        claims: {},
        nvt: true,
      }
    );
  },
});
export default voorraadbeheerStateCreator;
